import { MediaType } from "@/generated/graphql";
import type { IconType } from "react-icons";
import { FiFile, FiHeadphones, FiImage, FiVideo } from "react-icons/fi";

/**
 * Table column identifiers for dynamic column rendering
 */
export type TableColumn =
  | "checkbox"
  | "preview"
  | "filename"
  | "audioText"
  | "duration"
  | "tags"
  | "url"
  | "actions";

/**
 * Configuration interface for each media type
 * Defines behavior, UI text, and feature flags
 */
export interface MediaTypeConfig {
  // Identity
  type: MediaType;
  label: string;
  labelPlural: string;
  icon: IconType;

  // File handling
  acceptedMimeTypes: string;
  maxFileSize: number; // bytes

  // Feature flags
  hasAudioText: boolean;
  hasDuration: boolean;
  hasTranscription: boolean; // For "To Text" button (future API)

  // Table configuration
  tableColumns: TableColumn[];

  // Linking configuration - which types can this type link to
  linkableTypes: MediaType[];

  // UI text
  uploadTitle: string;
  editTitle: string;
  emptyStateMessage: string;
  trashEmptyMessage: string;
}

/**
 * Main configuration object for all media types
 */
export const MEDIA_CONFIG: Record<MediaType, MediaTypeConfig> = {
  [MediaType.Audio]: {
    type: MediaType.Audio,
    label: "Audio",
    labelPlural: "Audio files",
    icon: FiHeadphones,
    acceptedMimeTypes: "audio/*",
    maxFileSize: 100 * 1024 * 1024, // 100MB
    hasAudioText: true,
    hasDuration: true,
    hasTranscription: true,
    tableColumns: ["checkbox", "preview", "filename", "audioText", "tags", "url", "actions"],
    linkableTypes: [MediaType.Image, MediaType.Video],
    uploadTitle: "Upload audio files",
    editTitle: "Edit Audio",
    emptyStateMessage: "No audio files in this folder",
    trashEmptyMessage: "No audio files in trash",
  },
  [MediaType.Video]: {
    type: MediaType.Video,
    label: "Video",
    labelPlural: "Videos",
    icon: FiVideo,
    acceptedMimeTypes: "video/*",
    maxFileSize: 500 * 1024 * 1024, // 500MB
    hasAudioText: true,
    hasDuration: true,
    hasTranscription: true,
    tableColumns: ["checkbox", "preview", "filename", "audioText", "tags", "url", "actions"],
    linkableTypes: [MediaType.Image, MediaType.Audio],
    uploadTitle: "Upload video files",
    editTitle: "Edit Video",
    emptyStateMessage: "No videos in this folder",
    trashEmptyMessage: "No videos in trash",
  },
  [MediaType.Image]: {
    type: MediaType.Image,
    label: "Image",
    labelPlural: "Images",
    icon: FiImage,
    acceptedMimeTypes: "image/*",
    maxFileSize: 50 * 1024 * 1024, // 50MB
    hasAudioText: false,
    hasDuration: false,
    hasTranscription: false,
    tableColumns: ["checkbox", "preview", "filename", "tags", "url", "actions"],
    linkableTypes: [MediaType.Audio, MediaType.Video],
    uploadTitle: "Upload images",
    editTitle: "Edit Image",
    emptyStateMessage: "No images in this folder",
    trashEmptyMessage: "No images in trash",
  },
  [MediaType.File]: {
    type: MediaType.File,
    label: "File",
    labelPlural: "Files",
    icon: FiFile,
    acceptedMimeTypes: "*/*",
    maxFileSize: 100 * 1024 * 1024, // 100MB
    hasAudioText: false,
    hasDuration: false,
    hasTranscription: false,
    tableColumns: ["checkbox", "preview", "filename", "tags", "url", "actions"],
    linkableTypes: [],
    uploadTitle: "Upload files",
    editTitle: "Edit File",
    emptyStateMessage: "No files in this folder",
    trashEmptyMessage: "No files in trash",
  },
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get the full configuration for a media type
 */
export function getMediaConfig(type: MediaType): MediaTypeConfig {
  return MEDIA_CONFIG[type];
}

/**
 * Check if a media type has audioText field
 */
export function hasAudioText(type: MediaType): boolean {
  return MEDIA_CONFIG[type].hasAudioText;
}

/**
 * Check if a media type has duration field
 */
export function hasDuration(type: MediaType): boolean {
  return MEDIA_CONFIG[type].hasDuration;
}

/**
 * Check if a media type supports transcription
 */
export function hasTranscription(type: MediaType): boolean {
  return MEDIA_CONFIG[type].hasTranscription;
}

/**
 * Get accepted MIME types for a media type
 */
export function getAcceptedMimeTypes(type: MediaType): string {
  return MEDIA_CONFIG[type].acceptedMimeTypes;
}

/**
 * Get max file size for a media type (in bytes)
 */
export function getMaxFileSize(type: MediaType): number {
  return MEDIA_CONFIG[type].maxFileSize;
}

/**
 * Get linkable types for a media type
 */
export function getLinkableTypes(type: MediaType): MediaType[] {
  return MEDIA_CONFIG[type].linkableTypes;
}

/**
 * Get table columns for a media type
 */
export function getTableColumns(type: MediaType): TableColumn[] {
  return MEDIA_CONFIG[type].tableColumns;
}

/**
 * Format file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number.parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
}

/**
 * Format duration in MM:SS format
 */
export function formatDuration(seconds: number | undefined | null): string {
  if (!seconds) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Get the icon component for a media type
 */
export function getMediaIcon(type: MediaType): IconType {
  return MEDIA_CONFIG[type].icon;
}

/**
 * Validate file against media type constraints
 */
export function validateFile(file: File, type: MediaType): { valid: boolean; error?: string } {
  const config = MEDIA_CONFIG[type];

  // Check file size
  if (file.size > config.maxFileSize) {
    return {
      valid: false,
      error: `File size exceeds ${formatFileSize(config.maxFileSize)} limit`,
    };
  }

  // Check MIME type (if not accepting all)
  if (config.acceptedMimeTypes !== "*/*") {
    const acceptedBase = config.acceptedMimeTypes.replace("/*", "");
    if (!file.type.startsWith(acceptedBase)) {
      return {
        valid: false,
        error: `Invalid file type. Expected ${config.label.toLowerCase()} file`,
      };
    }
  }

  return { valid: true };
}
